import React, { useEffect } from 'react';
import InputField from '../../../../components/controls/Form/components/InputField';
import CheckboxField from '../../../../components/controls/Form/components/CheckboxField';
import DropdownField from '../../../../components/controls/Form/components/DropdownField';
import RadioField from '../../../../components/controls/Form/components/RadioField';
import TextAreaField from '../../../../components/controls/Form/components/TextAreaField';
import FormGroup from '../../../../components/controls/Form/components/FormGroup';
import UploadDocument from './UploadDocument';
import { createMarkup } from '../../../../utils/validateForm';
import { COMMENT_TEXT_LIMIT } from '../../../../utils/Constants';
import { getFileNameWithoutExtension } from '../../../../utils/fileUtils';
import useRecommendation, { useQuestionById } from '../../../../utils/useRecommendation';

import './Question.less';

const commentToQIDMap = (/** @type {Map<String,String>} */(new Map()));
const checkedOptions = (/** @type {Set<String>} */(new Set()));

/**
 * @typedef {import('../../../../store/reducers/dashboardReducer').TEvaluationBlockQuestion} TEvaluationBlockQuestion
 * @param {{ questionId:number }} props
 * @returns
 */
const Question = (props) => {
  const { questionId } = props;
  const recommendation = useRecommendation();
  const blockName = recommendation.getBlockByQuestionId(questionId)?.blockName;
  const questionWrapper = useQuestionById(questionId);

  /**
   *
   * @param {string} name
   * @param {null|string|boolean|undefined} answer
   */
  const handleSingleAnswerChange = (name, answer) => {
    questionWrapper.setUserSingleAnswer(answer);
  }

  /**
   *
   * @param {TEvaluationBlockQuestion=} question
   * @param {Record<string, any>=} params
   * @returns {React.ReactElement=}
   */
  const getQuestionByType = (question, params) => {
    switch (question?.questionTypeValue) {
      case 'Dropdown':
        // populate existing comment for the current question
        commentToQIDMap.has(question.id.toString()) ? void 0 :
          commentToQIDMap.set(question.id.toString(), question.userAnswer?.[0]?.comments ?? '');
        return (
          <DropdownField
            {...params}
            ariaLabel={question.questionText}
            ariaDescribedBy={'lsn-question-item-title-' + question.id}
            title={question.questionText}
            customErrorPrefix='Entry'
            showDefault={true}
            selectedValue={questionWrapper.getFirstUserAnswer()?.toString()}
            onChange={(_, value) => {
              const optionDetails = questionWrapper.getQuestionDetailsByQuestionAnswer(value);
              questionWrapper.setUserSingleAnswer(value, optionDetails?.commentsEnabled ? commentToQIDMap.get(question.id.toString()) ?? null : null);
              checkedOptions.add(question.id.toString());
            }}
            options={(question.evaluationQuestionDetails ?? [])
              .map(eq => {
                return { value: eq.answerOption ?? '', title: eq.answerOption + '' };
              })}
            postControlSlot={(selectedOption) => {
              const optionDetails = questionWrapper.getQuestionDetailsByQuestionAnswer(selectedOption?.value);
              return optionDetails?.commentsEnabled ? <div className='question-answer-comment-wrapper'>
                <TextAreaField
                  required={optionDetails.commentsRequired && !!question.userAnswer?.find(u => u.answer === optionDetails.answerOption)}
                  limit={COMMENT_TEXT_LIMIT}
                  autoExpand
                  autoSelectWhenEnabled={checkedOptions.has(question.id.toString())}
                  title='Comment'
                  disabled={!question.userAnswer?.find(u => u.answer === optionDetails.answerOption)}
                  name={question.id.toString()}
                  onChange={(name, comments) => {
                    // keep comments in cache for the question
                    commentToQIDMap.set(name, comments);
                    questionWrapper.setAnswerComment(optionDetails.answerOption, comments);
                  }}
                  placeholder={optionDetails.commentsLabel ?? ''}
                  // use answer-comment or the cached comment.
                  value={question.userAnswer?.find(u => u.answer === optionDetails.answerOption)?.comments ?? commentToQIDMap.get(optionDetails.id.toString())} />
                <i className={`far fa-comment-alt-lines`}></i>
              </div> : null
            }}
          />
        );
      case 'Checkbox':
        return (<CheckboxField
          {...params}
          ariaLabel={question.questionText}
          ariaDescribedBy={'lsn-question-item-title-' + question.id}
          title={question.questionText}
          customErrorPrefix='Entry'
          name='test'
          options={(question.evaluationQuestionDetails ?? [])
            .filter(o => o.answerOption)
            .sort((a, b) => a.sortId - b.sortId)
            .map(option => {
              const comments = question.userAnswer?.find(u => u.answer === option.answerOption)?.comments ?? '';
              // heat the comments-cache when the question is rendered for the first time and the comment is not in cache, otherwise use the cached comment
              commentToQIDMap.has(option.id.toString()) ? void 0 : commentToQIDMap.set(option.id.toString(), comments);
              return option;
            })
            .map(option => ({
              checked: !!question.userAnswer?.find(u => u.answer === option.answerOption),
              label: option.answerOption ?? '',
              title: option.answerOption ?? '',
              value: option.answerOption ?? '',
              postOptionSlot: option.commentsEnabled ?
                <div className='question-answer-comment-wrapper'>
                  <TextAreaField
                    required={option.commentsRequired && !!question.userAnswer?.find(u => u.answer === option.answerOption)}
                    limit={COMMENT_TEXT_LIMIT}
                    autoExpand
                    autoSelectWhenEnabled={checkedOptions.has(option.id.toString())}
                    title='Comment'
                    disabled={!question.userAnswer?.find(u => u.answer === option.answerOption)}
                    name={option.id.toString()}
                    onChange={(name, comments) => {
                      // keep comments in cache for the particular checkbox
                      commentToQIDMap.set(name, comments);
                      questionWrapper.setAnswerComment(option.answerOption, comments);
                    }}
                    placeholder={option.commentsLabel ?? ''}
                    // use answer-comment or the cached comment
                    value={question.userAnswer?.find(u => u.answer === option.answerOption)?.comments ?? commentToQIDMap.get(option.id.toString())} />
                  <i className={`far fa-comment-alt-lines`}></i>
                </div> : null
            }))}
          onChange={(_, checkState, answer) => {
            const optionId = question.evaluationQuestionDetails?.find(o => o.answerOption === answer)?.id.toString() ?? '';
            checkState ? checkedOptions.add(optionId) : checkedOptions.delete(optionId);
            checkState ? questionWrapper.addNewUserAnswer(answer, commentToQIDMap.get(optionId)) : questionWrapper.removeUserAnswer(answer);
          }}
        />
        );
      case 'Text':
        return (
          <InputField
            {...params}
            ariaLabel={question.questionText}
            ariaDescribedBy={'lsn-question-item-title-' + question.id}
            title={question.questionText}
            customErrorPrefix='Entry'
            value={questionWrapper.getFirstUserAnswer()?.toString()}
            limit={question.evaluationQuestionDetails?.[0].maxChars}
            onChange={handleSingleAnswerChange}
          />
        );
      case 'Essay':
        return (
          <TextAreaField
            {...params}
            ariaLabel={question.questionText}
            ariaDescribedBy={'lsn-question-item-title-' + question.id}
            title={question.questionText}
            customErrorPrefix='Entry'
            value={questionWrapper.getFirstUserAnswer()?.toString()}
            limit={question.evaluationQuestionDetails?.[0].maxChars}
            onChange={handleSingleAnswerChange}
          />
        );
      case 'Radio':
        // populate existing comment for the current question
        commentToQIDMap.has(question.id.toString()) ? void 0 :
          commentToQIDMap.set(question.id.toString(), question.userAnswer?.[0]?.comments ?? '');
        return (
          <RadioField
            {...params}
            ariaLabel={question.questionText}
            ariaDescribedBy={'lsn-question-item-title-' + question.id}
            name={question.id.toString()}
            customErrorPrefix='Entry'
            classNameWrapper={'flex'}
            selectedOptionValue={questionWrapper.getFirstUserAnswer()?.toString()}
            onChange={(_, value) => {
              const option = question.evaluationQuestionDetails?.find(o => o.answerOption === value);
              questionWrapper.setUserSingleAnswer(value, option?.commentsEnabled ? commentToQIDMap.get(question.id.toString()) ?? null : null);
              checkedOptions.add(question.id.toString());
            }}
            options={(question.evaluationQuestionDetails ?? [])
              .map(option => ({ value: option.answerOption, label: option.answerOption }))}
            horizontal
            postControlSlot={(selectedOption) => {
              const optionDetails = questionWrapper.getQuestionDetailsByQuestionAnswer(selectedOption?.value);
              return optionDetails?.commentsEnabled ? <div className='question-answer-comment-wrapper'>
                <TextAreaField
                  required={optionDetails.commentsRequired && !!question.userAnswer?.find(u => u.answer === optionDetails.answerOption)}
                  limit={COMMENT_TEXT_LIMIT}
                  autoExpand
                  autoSelectWhenEnabled={checkedOptions.has(question.id.toString())}
                  title='Comment'
                  disabled={!question.userAnswer?.find(u => u.answer === optionDetails.answerOption)}
                  name={question.id.toString()}
                  onChange={(name, comments) => {
                    // keep comments in cache for the question
                    commentToQIDMap.set(name, comments);
                    questionWrapper.setAnswerComment(optionDetails.answerOption, comments);
                  }}
                  placeholder={optionDetails.commentsLabel ?? ''}
                  // use answer-comment or the cached comment
                  value={question.userAnswer?.find(u => u.answer === optionDetails.answerOption)?.comments ?? commentToQIDMap.get(question.id.toString())} />
                <i className={`far fa-comment-alt-lines`}></i>
              </div> : null
            }}
          />
        );
      case 'File Upload':
        return (
          <UploadDocument
            {...params}
            ariaLabel={question.questionTypeValue + '. ' + question.questionText}
            ariaDescribedBy={'lsn-question-item-title-' + question.id}
            value={
              // temporary hack to show confirmed uploaded info
              question?.userAnswer?.length &&
                recommendation.item?.evaluationResponse?.id &&
                recommendation.item?.evaluationResponse?.fileUploadedDate &&
                recommendation.item?.evaluationResponse?.fileName ? {
                id: recommendation.item?.evaluationResponse?.id,
                fileName: recommendation.item?.evaluationResponse?.fileName,
                fileUploadedDate: recommendation.item?.evaluationResponse?.fileUploadedDate,
              } : null
            }
            onChange={(fileInfo) => {
              fileInfo ? questionWrapper.setUserSingleAnswer(fileInfo?.id ?? null) : questionWrapper.clearUserAnswer();
            }}
            onUploadResponse={(responseUpload) => {
              responseUpload ? questionWrapper.setEvaluationResponse(responseUpload) : questionWrapper.removeEvaluationResponse();
            }}
            requireConfirmation
            previewUrl={recommendation.item?.evaluationResponse?.id && recommendation.item.evaluationResponse.fileName ? [
              '/recommendation/rest/recommendationForm/document/view',
              recommendation.item.evaluationResponse.id,
              getFileNameWithoutExtension(recommendation.item.evaluationResponse.fileName)
            ].filter(Boolean).join('/') : null}
            deleteEndpoint={`/recommendation/rest/recommendationForm/document/${recommendation.item?.evaluationResponse?.id}`}
            uploadEndpoint={`/recommendation/rest/recommendationForm/document/${recommendation.item?.recommendationRequest.id}/evaluationQuestion/${question.id}`}
          />
        );
      default: return void 0;
    }
  }

  // clear the cache on unmount
  useEffect(() => {
    return () => {
      commentToQIDMap.clear();
      checkedOptions.clear();
    }
  }, []);

  return questionWrapper.question ? <FormGroup
    id={`lsn_eval_control_${questionWrapper.question.id}`}
    label={<span
      id={'lsn-question-item-title-' + questionWrapper.question.id}
      dangerouslySetInnerHTML={createMarkup(questionWrapper.question.questionText)}
    ></span>}
    classNameControlWrapper={questionWrapper.question.questionTypeValue === 'File Upload' ? '' : 'col-9 col-md-12'}
    classNameWrapper={'lsn-question-block question-item'}
    required={questionWrapper.question.required}
    controlRender={params => getQuestionByType(questionWrapper.question, { ...params, })}
    metadata={{
      'q-type': questionWrapper.question.questionTypeValue,
      'block-name': blockName
    }}
  /> : null
}

export default Question;
